/**
 * MobileCardList 鈥?Native mobile card-list replacement for antd Table.
 *
 * Renders each dataSource record as a card with label-value fields,
 * pagination, row selection, and expandable rows.
 *
 * Props are a subset of antd Table + a few mobile-specific additions.
 */
import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Button, Checkbox, Spin, Empty, Pagination } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import "./styles.css";

/* ---------- helpers ---------- */

const getRowKey = (record, rowKey, index) => {
  if (typeof rowKey === "function") return rowKey(record);
  if (typeof rowKey === "string") return record[rowKey];
  return String(index);
};

/** Resolve nested dataIndex like "user.name" from record */
const getNestedValue = (obj, path) => {
  if (!path) return undefined;
  if (typeof path !== "string") return obj[path];
  return path.split(".").reduce((acc, key) => (acc != null ? acc[key] : undefined), obj);
};

const getCellValue = (record, col, index, action) => {
  const rawValue = col.dataIndex ? getNestedValue(record, col.dataIndex) : undefined;

  // ProTable valueEnum mapping
  if (col.valueEnum && rawValue != null) {
    const entry = col.valueEnum[rawValue];
    if (entry && typeof entry === "object") return entry.text ?? rawValue;
    if (typeof entry === "string") return entry;
  }

  if (col.render) return col.render(rawValue, record, index, action);

  if (col.dataIndex) {
    if (rawValue === undefined || rawValue === null) return "-";
    if (typeof rawValue === "boolean") return String(rawValue);
    return rawValue;
  }
  return null;
};

const getColumnKey = (col) => {
  const rawKey = col.key ?? col.dataIndex;
  if (Array.isArray(rawKey)) return rawKey.join(".");
  if (rawKey == null) return "";
  return String(rawKey);
};

const flattenRows = (rows, childrenColumnName) =>
  (rows || []).flatMap((record) => {
    const children = record?.[childrenColumnName];
    return Array.isArray(children) && children.length > 0
      ? [record, ...flattenRows(children, childrenColumnName)]
      : [record];
  });

/* ---------- component ---------- */

const MobileCardList = (props) => {
  const {
    columns = [],
    dataSource = [],
    loading = false,
    rowKey = "id",
    rowSelection,
    pagination,
    expandable,
    onRow,
    title,
    footer,
    className = "",
    style,
    size = "default",
    locale,
    mobileRowActions,
    mobileInfiniteScroll = false,
    onLoadMore,
    hasMore = false,
    loadingMore = false,
    loadMoreError = false,
    topToolbar,
    tableAction,
    mobileCardCollapsible = true,
    hideMobileColumnActions = false,
    hideMobileTableExpand = false,
    mobileCardHeaderRender,
    mobileTitleOnClick,
    mobileExpandedExcludeKeys = [],
    mobileExpandedIncludeKeys = [],
  } = props;

  /* ---- local state ---- */
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [cardExpandedKeys, setCardExpandedKeys] = useState([]);

  const emptyText = locale?.emptyText ?? "No data";
  const childrenColumnName = expandable?.childrenColumnName || "children";

  /* ---- classify columns ---- */
  const { titleCol, infoCols, actionCols } = useMemo(() => {
    let titleCol = null;
    const infoCols = [];
    const actionCols = [];
    const excludeKeySet = new Set((mobileExpandedExcludeKeys || []).map(String));
    const includeKeySet = new Set((mobileExpandedIncludeKeys || []).map(String));

    columns.forEach((col) => {
      const colKey = getColumnKey(col);
      // skip selection & expand columns
      if (col.key === "selection" || col.key === "expand") return;
      if (col.type === "selection" || col.type === "expand") return;
      if (col.dataIndex === "ant-table-selection-column" || col.dataIndex === "ant-table-expand-icon") return;
      if (excludeKeySet.has(colKey)) return;

      // ProTable hideInTable/index
      if (col.hideInTable && !includeKeySet.has(colKey)) return;
      if (col.valueType === "index" || col.valueType === "indexBorder") return;

      // ProTable option columns are row actions.
      if (col.valueType === "option") {
        actionCols.push(col);
        return;
      }

      // Detect action column: title is empty and has a render function
      if (!col.title && col.render && !col.dataIndex) {
        actionCols.push(col);
        return;
      }

      // First column with dataIndex becomes title
      if (!titleCol && (col.dataIndex || col.render)) {
        titleCol = col;
        if (includeKeySet.has(colKey)) {
          infoCols.push(col);
        }
        return;
      }

      if (col.dataIndex || col.render) {
        infoCols.push(col);
      }
    });

    return { titleCol, infoCols, actionCols };
  }, [columns, mobileExpandedExcludeKeys, mobileExpandedIncludeKeys]);

  /* ---- data ---- */
  const processedData = useMemo(() => {
    return [...dataSource];
  }, [dataSource]);
  const flattenedData = useMemo(
    () => flattenRows(processedData, childrenColumnName),
    [processedData, childrenColumnName]
  );

  /* ---- pagination ---- */
  const isRemotePagination =
    pagination !== false &&
    pagination &&
    typeof pagination.onChange === "function";
  const pageSize = pagination?.pageSize ?? pagination?.defaultPageSize ?? 20;
  const totalItems = isRemotePagination
    ? pagination.total ?? processedData.length
    : processedData.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const pagedData = useMemo(() => {
    if (pagination === false || isRemotePagination || mobileInfiniteScroll) {
      return processedData;
    }
    const start = (currentPage - 1) * pageSize;
    return processedData.slice(start, start + pageSize);
  }, [processedData, currentPage, pageSize, pagination, isRemotePagination, mobileInfiniteScroll]);

  /* ---- selection ---- */
  const selectedKeys = rowSelection?.selectedRowKeys ?? [];
  const onSelectChange = rowSelection?.onChange;
  const getCheckboxProps = rowSelection?.getCheckboxProps;

  const isRecordSelectable = useCallback(
    (record) => !getCheckboxProps?.(record)?.disabled,
    [getCheckboxProps]
  );

  const getRowsByKeys = useCallback(
    (keys) => flattenedData.filter((record, index) =>
      keys.includes(getRowKey(record, rowKey, index))
    ),
    [flattenedData, rowKey]
  );

  const handleSelect = useCallback(
    (key, checked, record) => {
      if (!onSelectChange || !isRecordSelectable(record)) return;
      const next = checked
        ? [...selectedKeys, key]
        : selectedKeys.filter((k) => k !== key);
      onSelectChange(next, getRowsByKeys(next));
    },
    [selectedKeys, onSelectChange, getRowsByKeys, isRecordSelectable]
  );

  const selectablePageRows = useMemo(
    () => flattenRows(pagedData, childrenColumnName).filter(isRecordSelectable),
    [pagedData, childrenColumnName, isRecordSelectable]
  );

  const handleSelectAll = useCallback(
    (checked) => {
      if (!onSelectChange) return;
      const next = checked
        ? selectablePageRows.map((record, index) => getRowKey(record, rowKey, index))
        : [];
      onSelectChange(next, checked ? selectablePageRows : []);
    },
    [selectablePageRows, rowKey, onSelectChange]
  );

  /* ---- expand ---- */
  const handleExpand = useCallback(
    (key, record) => {
      setExpandedKeys((prev) =>
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
      );
      if (typeof expandable?.onExpand === "function") {
        expandable.onExpand(!expandedKeys.includes(key), record);
      }
    },
    [expandable, expandedKeys]
  );

  const handleCardExpand = useCallback(
    (key) => {
      if (!mobileCardCollapsible) return;
      setCardExpandedKeys((prev) =>
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
      );
    },
    [mobileCardCollapsible]
  );

  const shouldIgnoreCardClick = (target) => {
    if (!target || typeof target.closest !== "function") return false;
    return Boolean(
      target.closest(
        "button, input, textarea, select, a, [role='button'], .ant-checkbox-wrapper, .mcl-card-title-link, .workorder-mobile-card-title-link, .equipment-mobile-card-title-link"
      )
    );
  };

  const loadMoreRef = useRef(null);
  useEffect(() => {
    if (!mobileInfiniteScroll || !onLoadMore || typeof IntersectionObserver === "undefined") {
      return undefined;
    }
    const node = loadMoreRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first?.isIntersecting && hasMore && !loading && !loadingMore) {
        onLoadMore();
      }
    }, { rootMargin: "160px 0px" });
    observer.observe(node);
    return () => observer.disconnect();
  }, [mobileInfiniteScroll, onLoadMore, hasMore, loading, loadingMore]);

  /* ---- render ---- */
  const isCompact = size === "small";

  const renderRecord = (record, index, depth = 0) => {
    const key = getRowKey(record, rowKey, index);
    const isExpanded = expandedKeys.includes(key);
    const isCardExpanded = !mobileCardCollapsible || cardExpandedKeys.includes(key);
    const isSelected = selectedKeys.includes(key);
    const rowProps = (onRow ? onRow(record, index) : {}) || {};
    const children = Array.isArray(record?.[childrenColumnName])
      ? record[childrenColumnName]
      : [];
    const hasTreeChildren = children.length > 0;
    const canExpandRow =
      typeof expandable?.rowExpandable !== "function" || expandable.rowExpandable(record);
    const hasExpandedRow = typeof expandable?.expandedRowRender === "function";
    const canExpandRecord = Boolean(
      expandable &&
      !hideMobileTableExpand &&
      canExpandRow &&
      (hasTreeChildren || hasExpandedRow)
    );
    const checkboxProps = getCheckboxProps?.(record) || {};
    const extraActions =
      typeof mobileRowActions === "function"
        ? mobileRowActions(record, index, {
            rowKey: key,
            selected: isSelected,
            expanded: isCardExpanded,
            tableExpanded: isExpanded,
          }) || []
        : [];
    const rowActions = [
      ...(hideMobileColumnActions
        ? []
        : actionCols.map((col) => getCellValue(record, col, index, tableAction))),
      ...React.Children.toArray(extraActions),
    ].filter((action) => action !== null && action !== undefined && action !== false);
    const headerNode =
      typeof mobileCardHeaderRender === "function"
        ? mobileCardHeaderRender(record, {
            index,
            rowKey: key,
            selected: isSelected,
            expanded: isCardExpanded,
            tableExpanded: isExpanded,
            title: titleCol ? getCellValue(record, titleCol, index, tableAction) : key,
            actions: rowActions,
            toggleCardExpand: () => handleCardExpand(key),
            toggleTableExpand: () => {
              if (canExpandRecord) handleExpand(key, record);
            },
          })
        : null;
    const handleTitleClick =
      typeof mobileTitleOnClick === "function"
        ? (event) => {
            event.stopPropagation();
            mobileTitleOnClick(record, index, {
              rowKey: key,
              selected: isSelected,
              expanded: isCardExpanded,
            });
          }
        : null;
    const handleRecordClick = (event) => {
      if (shouldIgnoreCardClick(event.target)) return;
      if (canExpandRecord && hasTreeChildren) {
        handleExpand(key, record);
        return;
      }
      handleCardExpand(key);
    };

    return (
      <div
        key={key}
        className={`mcl-tree-node ${depth > 0 ? "mcl-tree-node-child" : ""}`.trim()}
      >
        <div
          className={[
            "mcl-card",
            isSelected ? "mcl-card-selected" : "",
            isCompact ? "mcl-card-compact" : "",
            hasTreeChildren ? "mcl-card-tree-parent" : "",
            rowProps.className || "",
          ].filter(Boolean).join(" ")}
          onClick={handleRecordClick}
        >
          <div
            className={[
              "mcl-card-header",
              mobileCardCollapsible ? "mcl-card-header-clickable" : "",
              headerNode ? "mcl-card-header-custom" : "",
            ].filter(Boolean).join(" ")}
            onClick={(event) => {
              event.stopPropagation();
              handleRecordClick(event);
            }}
          >
            {rowSelection && (
              <Checkbox
                {...checkboxProps}
                checked={isSelected}
                onChange={(event) => {
                  event.stopPropagation();
                  handleSelect(key, event.target.checked, record);
                }}
                onClick={(event) => event.stopPropagation()}
              />
            )}
            {headerNode || (
              <span
                className={`mcl-card-title ${handleTitleClick ? "mcl-card-title-link" : ""}`}
                onClick={handleTitleClick}
              >
                {titleCol ? getCellValue(record, titleCol, index, tableAction) : key}
              </span>
            )}
            {canExpandRecord && (
              <Button
                type="text"
                size="small"
                className="mcl-expand-btn"
                onClick={(event) => {
                  event.stopPropagation();
                  handleExpand(key, record);
                }}
                icon={<span className={`mcl-expand-icon ${isExpanded ? "mcl-expanded" : ""}`} />}
                aria-label={isExpanded ? "Collapse row" : "Expand row"}
                aria-expanded={isExpanded}
              />
            )}
          </div>

          {isCardExpanded && infoCols.length > 0 && (
            <div className="mcl-card-body">
              {infoCols.map((col) => (
                <div
                  key={getColumnKey(col)}
                  className={`mcl-field${col.hideMobileLabel ? " mcl-field-value-only" : ""}`}
                >
                  {!col.hideMobileLabel && <span className="mcl-field-label">{col.title}</span>}
                  <div className="mcl-field-value">
                    {getCellValue(record, col, index, tableAction)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {isCardExpanded && rowActions.length > 0 && (
            <div className="mcl-card-footer">
              {rowActions.map((action, actionIndex) => (
                <span
                  key={action?.key || actionIndex}
                  className="mcl-action"
                  onClick={(event) => event.stopPropagation()}
                >
                  {action}
                </span>
              ))}
            </div>
          )}

          {canExpandRecord && isExpanded && hasExpandedRow && (
            <div className="mcl-card-expanded">
              {expandable.expandedRowRender(record, index, depth, isExpanded)}
            </div>
          )}
        </div>

        {canExpandRecord && isExpanded && hasTreeChildren && (
          <div className="mcl-tree-children">
            {children.map((child, childIndex) => renderRecord(child, childIndex, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`mobile-card-list ${className}`} style={style}>
      {topToolbar && (
        <div className="mcl-top-toolbar">
          {topToolbar}
        </div>
      )}

      {/* ---- Title (from original table title prop) ---- */}
      {typeof title === "function" ? title(pagedData) : title}

      {/* ---- Select all ---- */}
      {rowSelection && pagedData.length > 0 && (
        <div className="mcl-select-all">
          <Checkbox
            checked={
              selectablePageRows.length > 0 &&
              selectablePageRows.every((record, index) =>
                selectedKeys.includes(getRowKey(record, rowKey, index))
              )
            }
            indeterminate={
              selectablePageRows.some((record, index) =>
                selectedKeys.includes(getRowKey(record, rowKey, index))
              ) &&
              !selectablePageRows.every((record, index) =>
                selectedKeys.includes(getRowKey(record, rowKey, index))
              )
            }
            onChange={(e) => handleSelectAll(e.target.checked)}
          >
            Select all
          </Checkbox>
          <span className="mcl-selected-count">
            {selectedKeys.length > 0 && `${selectedKeys.length} selected`}
          </span>
        </div>
      )}

      {/* ---- Card List ---- */}
      <Spin spinning={loading} indicator={<LoadingOutlined />}>
        <div className="mcl-card-list">
          {pagedData.length === 0 && !loading ? (
            <Empty description={emptyText} className="mcl-empty" />
          ) : (
            pagedData.map((record, index) => renderRecord(record, index))
          )}
        </div>
      </Spin>

      {mobileInfiniteScroll && (
        <div className="mcl-infinite-status" ref={loadMoreRef}>
          {loadingMore ? (
            <span>Loading...</span>
          ) : loadMoreError ? (
            <Button size="small" onClick={onLoadMore}>
              Retry
            </Button>
          ) : hasMore ? (
            <span>Pull up to load more</span>
          ) : pagedData.length > 0 ? (
            <span>No more data</span>
          ) : null}
        </div>
      )}

      {/* ---- Pagination ---- */}
      {!mobileInfiniteScroll && pagination !== false && totalPages > 1 && (
        <div className="mcl-pagination">
          <Pagination
            current={isRemotePagination ? pagination.current : currentPage}
            total={totalItems}
            pageSize={pageSize}
            size="small"
            simple
            onChange={(page) => {
              if (isRemotePagination) {
                pagination.onChange(page, pageSize);
              } else {
                setCurrentPage(page);
              }
            }}
            showSizeChanger={false}
          />
        </div>
      )}

      {/* ---- Footer ---- */}
      <div className="mcl-footer">
        {typeof footer === "function" ? footer(pagedData) : footer}
      </div>
    </div>
  );
};

export default MobileCardList;



